/**
 * Sync mode, its first half (piece 31; decision 3800; T2294): a switch with an
 * end date, off by default, that turns on the daily pull of what changed in
 * Zoho Books. The push into Zoho Books is the second half and is not built.
 */
import { t } from '../strings';
import { ZOHO_BOOKS_IMPORT_PATH } from './importRun';
import { isCalendarDay } from '../zonedTime';

/** 🔒 The switch, an Organization row neither seeded nor written by the generic Settings write; absent reads off (decision 3800). */
export const ZOHO_BOOKS_SYNC_ENABLED_KEY = 'zohoBooks.syncEnabled';
/** 🔒 The last calendar day the daily pull runs, in the organization's zone (decision 3800). */
export const ZOHO_BOOKS_SYNC_UNTIL_KEY = 'zohoBooks.syncUntil';
/** The two rows `PUT /import/zoho-books/sync` writes, and nothing else does. */
export const ZOHO_BOOKS_SYNC_KEYS: readonly string[] = [ZOHO_BOOKS_SYNC_ENABLED_KEY, ZOHO_BOOKS_SYNC_UNTIL_KEY];

/** 🔒 The switch's one writer, behind the importer's administrative capability (decision 3800). */
export const ZOHO_BOOKS_SYNC_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/sync`;

/** 🔒 The daily pull's last report (decision 3801): when it ran, and its one line per block. */
export interface ZohoBooksSyncPull {
  at: string;
  summary: string;
}

/** What the Connect step reads of sync mode. */
export interface ZohoBooksSyncView {
  /** The switch as it is saved. */
  on: boolean;
  /** The last day the pull runs, a calendar day; null where none is saved. */
  until: string | null;
  /** Whether the pull runs today: on, with an end date not yet past. */
  running: boolean;
  /** The daily pull's last report; absent or null where it never ran. */
  lastPull?: ZohoBooksSyncPull | null;
}

/** The switch's two rows as the step reads them, a value of any other shape read as none. */
export function zohoBooksSyncView(on: unknown, until: unknown, today: string): ZohoBooksSyncView {
  const day = isCalendarDay(until) ? until : null;
  const enabled = on === true;
  return { on: enabled, until: day, running: enabled && day !== null && day >= today };
}

/**
 * 🔨 Why the switch may not be saved as asked, or null (decision 3800). It is
 * refused on while the preset has no source, with the sentence a refused Read
 * gives, and refused on with no end date or one before today. Off is never refused.
 */
export function zohoBooksSyncProblem(input: { on: unknown; until: unknown; today: string; sourceRefusal: string | null }): string | null {
  if (typeof input.on !== 'boolean') return 'The daily pull is either on or off.';
  if (input.until !== null && input.until !== undefined && input.until !== '' && !isCalendarDay(input.until)) return 'The end date is a calendar day, such as 2026-11-30.';
  if (!input.on) return null;
  if (input.sourceRefusal) return input.sourceRefusal;
  if (!isCalendarDay(input.until)) return 'The daily pull needs an end date, the last day it runs; choose one, then turn it on.';
  if (input.until < input.today) return `The end date ${input.until} has passed; choose today or a later day, then turn the daily pull on.`;
  return null;
}

/** 🔒 The Connect step's summary line of the switch (decision 3800), so it reads with the step closed. */
export function zohoBooksSyncLine(view: ZohoBooksSyncView): string {
  if (!view.on) return t('import.sync.off');
  if (!view.running) return `${t('import.sync.ended')} ${view.until ?? ''}`.trim();
  return `${t('import.sync.onUntil')} ${view.until}`;
}
